// Thin wrapper around pc.Picker for hotspot selection.
//
// Why this exists:
// • The pre-Sprint-1 implementation used a hand-rolled angular raycast in
//   HotspotManager.pickFromScreen() to resolve canvas taps to hotspots. That
//   worked but was an extra code path to maintain alongside the engine's own
//   pixel-perfect picker.
// • pc.Picker reads back the actual rendered pixels from a dedicated picking
//   buffer and tells you exactly which mesh instance is under the cursor.
//   No angular tolerance to tune, no off-by-one between visible 3D dot and
//   click target — if you can see it, you can click it.
// • Filtered to a dedicated layer (`hotspots`) so the picker only renders
//   hotspot meshes — not the panorama sphere — which is both fast and avoids
//   accidentally "picking" the sphere when the user taps empty wall.

import * as pc from 'playcanvas';

const PICK_REGION_PX = 8; // 8×8 sample around the tap → forgiving to mis-aims

export class HotspotPicker {
  private readonly app: pc.Application;
  private readonly cameraComponent: pc.CameraComponent;
  private readonly canvas: HTMLCanvasElement;
  private readonly layer: pc.Layer;
  private picker: pc.Picker | null = null;

  constructor(
    app: pc.Application,
    cameraEntity: pc.Entity,
    canvas: HTMLCanvasElement,
    layer: pc.Layer,
  ) {
    if (!cameraEntity.camera) {
      throw new Error('[picker] camera entity has no camera component');
    }
    this.app = app;
    this.cameraComponent = cameraEntity.camera;
    this.canvas = canvas;
    this.layer = layer;
  }

  /**
   * Pick the topmost mesh instance under the given client (CSS-pixel) coords.
   * Returns the owning Entity, or null if nothing in the hotspot layer was hit.
   */
  pickAtClient(clientX: number, clientY: number): pc.Entity | null {
    const device = this.app.graphicsDevice;
    const w = Math.max(1, device.width);
    const h = Math.max(1, device.height);

    if (!this.picker) {
      this.picker = new pc.Picker(this.app, w, h);
    } else if (this.picker.width !== w || this.picker.height !== h) {
      this.picker.resize(w, h);
    }

    // Convert client coords (CSS pixels) → backing-store pixels (top-left origin).
    const rect = this.canvas.getBoundingClientRect();
    const cssX = clientX - rect.left;
    const cssY = clientY - rect.top;
    const ratio = w / Math.max(1, rect.width);
    const x = cssX * ratio;
    const y = cssY * ratio;

    this.picker.prepare(this.cameraComponent, this.app.scene, [this.layer]);

    // Sample an 8×8 region centred on the tap so users don't have to be pixel-perfect.
    const half = PICK_REGION_PX / 2;
    const selection = this.picker.getSelection(
      Math.max(0, Math.round(x - half)),
      Math.max(0, Math.round(y - half)),
      PICK_REGION_PX,
      PICK_REGION_PX,
    );

    if (selection.length === 0) return null;
    const hit = selection[0];
    if (!hit) return null;
    // Selection items are MeshInstance | GSplatComponent. Both expose a backing
    // entity through `node`/`entity`. We currently only put plain meshes on the
    // hotspot layer, so MeshInstance.node is the canonical path.
    const node =
      'node' in hit ? hit.node : 'entity' in hit ? (hit as { entity: pc.Entity }).entity : null;
    if (!node) return null;
    return node as pc.Entity;
  }

  destroy(): void {
    this.picker?.destroy();
    this.picker = null;
  }
}
